import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, Mail, CheckCircle2, Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile-shell">
      <div className="px-6 pt-6 pb-2 flex items-center gap-3">
        <Link to="/login" data-testid="forgot-back-btn"><ArrowLeft /></Link>
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">
          Reset Password
        </span>
      </div>

      <div className="px-6 pt-4 pb-8">
        <div className="w-14 h-14 rounded-full bg-[#3CFF7E]/10 border border-[#3CFF7E]/40 flex items-center justify-center mb-4">
          {submitted ? (
            <CheckCircle2 className="text-[#3CFF7E]" size={26} />
          ) : (
            <Mail className="text-[#3CFF7E]" size={24} />
          )}
        </div>
        <h1 className="font-display text-3xl font-black tracking-tight leading-tight">
          {submitted ? "Check your email" : "Forgot your password?"}
        </h1>
        <p className="text-sm text-[#9CA3AF] mt-2 leading-relaxed">
          {submitted
            ? "If an account exists for that email, we've sent a reset link. It expires in 30 minutes."
            : "Enter the email you signed up with and we'll send you a link to choose a new password."}
        </p>
      </div>

      {!submitted ? (
        <form onSubmit={submit} className="px-6 space-y-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9CA3AF] font-display">Email</label>
            <input
              type="email"
              required
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="forgot-email-input"
              className="w-full h-12 mt-1 border border-white/20 rounded-md px-4 bg-transparent focus:outline-none focus:border-[#3CFF7E] focus:ring-2 focus:ring-[#3CFF7E]/20"
            />
          </div>
          {error && (
            <div className="text-sm text-[#F87171] border-l-2 border-[#F87171] pl-3" data-testid="forgot-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="forgot-submit-btn"
            className="w-full h-14 bg-[#3CFF7E] text-[#0A0A0A] font-display font-bold uppercase tracking-wider rounded-md active:scale-[0.98] transition-transform disabled:opacity-60 shadow-[0_0_40px_rgba(60,255,126,0.35)] flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {loading ? "Sending…" : "Send Reset Link"}
          </button>
        </form>
      ) : (
        <div className="px-6">
          <Link
            to="/login"
            data-testid="forgot-back-to-login"
            className="block w-full h-14 border border-white/20 text-white font-display font-bold uppercase tracking-wider rounded-md flex items-center justify-center"
          >
            Back to Sign In
          </Link>
        </div>
      )}
    </div>
  );
}
